// components/groups/JobPostingForm.jsx - Updated with Resource Requirements
import React, { useState, useEffect } from 'react';
import { X, Plus, Search, Calendar, Clock, MapPin, AlertCircle, HelpCircle, Loader, Truck, Wrench, Building } from 'lucide-react';
import { useSkills } from '../../context/SkillContext';
import { useResources } from '../../context/ResourceContext';
import { useAdmin } from '../../context/AdminContext';
import { useTrust } from '../../context/TrustContext';
import SkillBadge from '../trust/SkillBadge';
import { 
  calculateMaxTokensForJob, 
  TOKEN_POLICIES,
  formatTokenAmount
} from '../../utils/tokenEconomy';

const JobPostingForm = ({ groupId, groupTitle, onClose, onSuccess }) => {
  const { skills } = useSkills();
  const { resourceCategories } = useResources();
  const { postJob, getGroupTokenBudget, getRemainingTokens } = useAdmin();
  const { userTrust } = useTrust();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tokenBudget, setTokenBudget] = useState({ monthly: 0, remaining: 0 });
  const [activeTab, setActiveTab] = useState('basic'); // basic, skills, resources, schedule, review
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    requiredSkills: [],
    preferredSkills: [],
    resourceRequirements: {
      vehicles: [],
      equipment: [],
      spaces: []
    },
    minTrustScore: 3.5,
    tokenReward: 50,
    estimatedHours: 2,
    schedule: {
      date: '',
      startTime: '',
      endTime: '',
      flexible: false
    },
    location: '',
    maxApplicants: 10,
    isPlatformJob: false,
    category: null // for platform jobs
  });
  
  const [skillSearch, setSkillSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showRequiredSkills, setShowRequiredSkills] = useState(false);
  const [showPreferredSkills, setShowPreferredSkills] = useState(false);
  const [showResourceSelector, setShowResourceSelector] = useState(false);
  const [selectedResourceCategory, setSelectedResourceCategory] = useState('vehicles');
  const [suggestedTokens, setSuggestedTokens] = useState(50);
  const [maxAllowedTokens, setMaxAllowedTokens] = useState(500);

  // Tab navigation
  const tabs = [
    { id: 'basic', label: 'Basic Info' },
    { id: 'skills', label: 'Skills' },
    { id: 'resources', label: 'Resources' },
    { id: 'schedule', label: 'Schedule' },
    { id: 'review', label: 'Review' }
  ];

  // Load token budget on mount
  useEffect(() => {
    loadTokenBudget();
  }, [groupId]);
  
  // Calculate suggested tokens when skills or hours change
  useEffect(() => {
    const calculated = calculateSuggestedTokenReward();
    setSuggestedTokens(calculated);
    
    // Calculate maximum allowed
    const max = calculateMaxTokensForJob(formData);
    setMaxAllowedTokens(max);
  }, [formData.requiredSkills, formData.estimatedHours]);

  const loadTokenBudget = async () => {
    try {
      const monthly = await getGroupTokenBudget(groupId);
      const remaining = await getRemainingTokens(groupId);
      setTokenBudget({ monthly, remaining });
    } catch (err) {
      console.error('Error loading token budget:', err);
    }
  };

  const calculateSuggestedTokenReward = () => {
    if (formData.requiredSkills.length === 0) {
      return TOKEN_POLICIES.MAX_TOKENS_BY_TIER[1].base;
    }
    
    const maxTier = Math.max(...formData.requiredSkills.map(s => s.tier));
    const tierPolicy = TOKEN_POLICIES.MAX_TOKENS_BY_TIER[maxTier];
    
    const base = tierPolicy.base;
    const hourly = tierPolicy.perHour * formData.estimatedHours;
    
    // Add bonus for resource requirements
    const resourceBonus = Object.values(formData.resourceRequirements)
      .flat().length * 10;
    
    return Math.min(base + hourly + resourceBonus, TOKEN_POLICIES.MAX_TOKENS_PER_JOB);
  };

  // Resource requirement handlers
  const addResourceRequirement = (category, requirement) => {
    setFormData(prev => ({
      ...prev,
      resourceRequirements: {
        ...prev.resourceRequirements,
        [category]: [...prev.resourceRequirements[category], requirement]
      }
    }));
  };

  const removeResourceRequirement = (category, index) => {
    setFormData(prev => ({
      ...prev,
      resourceRequirements: {
        ...prev.resourceRequirements,
        [category]: prev.resourceRequirements[category].filter((_, i) => i !== index)
      }
    }));
  };

  // Get skill categories
  const categories = ['all', ...new Set(skills.map(skill => skill.category))];
  
  // Filter skills for display
  const getAvailableSkills = () => {
    let filtered = skills;
    
    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(skill => skill.category === selectedCategory);
    }
    
    // Filter by search
    if (skillSearch) {
      filtered = filtered.filter(skill => 
        skill.name.toLowerCase().includes(skillSearch.toLowerCase())
      );
    }
    
    // Exclude already selected skills
    const selectedIds = [
      ...formData.requiredSkills.map(s => s.id),
      ...formData.preferredSkills.map(s => s.id)
    ];
    
    return filtered.filter(skill => !selectedIds.includes(skill.id));
  };

  const handleSkillSelect = (skill, type) => {
    if (type === 'required') {
      setFormData(prev => ({
        ...prev,
        requiredSkills: [...prev.requiredSkills, skill]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        preferredSkills: [...prev.preferredSkills, skill]
      }));
    }
    setSkillSearch('');
  };

  const handleSkillRemove = (skillId, type) => {
    if (type === 'required') {
      setFormData(prev => ({
        ...prev,
        requiredSkills: prev.requiredSkills.filter(s => s.id !== skillId)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        preferredSkills: prev.preferredSkills.filter(s => s.id !== skillId)
      }));
    }
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      setError('Job title is required');
      return false;
    }
    
    if (!formData.description.trim()) {
      setError('Job description is required');
      return false;
    }
    
    if (formData.requiredSkills.length === 0) {
      setError('At least one required skill must be selected');
      return false;
    }
    
    if (!formData.schedule.date) {
      setError('Job date is required');
      return false;
    }
    
    if (!formData.location.trim()) {
      setError('Location is required');
      return false;
    }
    
    if (formData.tokenReward < 10) {
      setError('Token reward must be at least 10');
      return false;
    }
    
    if (formData.tokenReward > maxAllowedTokens) {
      setError(`Token reward cannot exceed ${maxAllowedTokens} for this job type`);
      return false;
    }
    
    if (formData.tokenReward > tokenBudget.remaining) {
      setError(`Insufficient token budget. You have ${tokenBudget.remaining} tokens remaining this month`);
      return false;
    }
    
    // Check trust requirement
    if (userTrust?.score < 3.5) {
      setError('You need a trust score of 3.5 or higher to post jobs');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      await postJob(groupId, {
        ...formData,
        groupTitle
      });
      
      // Reload token budget
      await loadTokenBudget();
      
      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Get qualified users estimate
  const getQualifiedUsersEstimate = () => {
    // In production, this would query the backend
    // For demo, return estimate based on skill rarity
    const avgTier = formData.requiredSkills.length > 0
      ? formData.requiredSkills.reduce((sum, skill) => sum + skill.tier, 0) / formData.requiredSkills.length
      : 1;
    
    const hasResourceReqs = Object.values(formData.resourceRequirements).some(reqs => reqs.length > 0);
    
    if (avgTier >= 3 || hasResourceReqs) return '2-5';
    if (avgTier >= 2) return '5-15';
    return '15+';
  };

  const renderBasicInfo = () => (
    <div className="space-y-4">
      {/* Platform Job Toggle */}
      {groupId === 'platform' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.isPlatformJob}
              onChange={(e) => setFormData(prev => ({ ...prev, isPlatformJob: e.target.checked }))}
              className="rounded"
            />
            <span className="font-medium">This is a platform job</span>
          </label>
          {formData.isPlatformJob && (
            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                value={formData.category || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">Select category</option>
                <option value="development">Development & Technical</option>
                <option value="content">Content & Creative</option>
                <option value="support">Community & Support</option>
                <option value="business">Business & Growth</option>
              </select>
            </div>
          )}
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Job Title *
        </label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="e.g., Weekend Pickup Coordinator"
          maxLength={100}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description *
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          rows={3}
          placeholder="Describe what the job involves..."
          maxLength={500}
        />
        <p className="text-xs text-gray-500 mt-1">
          {formData.description.length}/500 characters
        </p>
      </div>
    </div>
  );

  const renderResources = () => (
    <div className="space-y-4">
      <div className="mb-4">
        <h3 className="text-sm font-medium text-gray-700 mb-2">
          Resource Requirements (Optional)
        </h3>
        <p className="text-xs text-gray-500 mb-3">
          Specify any vehicles, equipment, or spaces needed for this job
        </p>
      </div>

      {/* Display selected resources */}
      {Object.entries(formData.resourceRequirements).map(([category, requirements]) => (
        requirements.length > 0 && (
          <div key={category} className="bg-gray-50 rounded-lg p-3">
            <h4 className="font-medium text-sm capitalize mb-2">{category}</h4>
            <div className="space-y-2">
              {requirements.map((req, index) => (
                <div key={index} className="flex items-center justify-between bg-white p-2 rounded border border-gray-200">
                  <div>
                    <span className="font-medium text-sm">{req.typeName}</span>
                    {req.minCapacity && (
                      <span className="text-xs text-gray-500 ml-2">
                        Min: {req.minCapacity} {req.capacityUnit}
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeResourceRequirement(category, index)}
                    className="text-red-500 hover:text-red-600"
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )
      ))}

      {/* Add resource button */}
      <button
        type="button"
        onClick={() => setShowResourceSelector(!showResourceSelector)}
        className="flex items-center gap-2 px-3 py-1.5 border border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-blue-500 hover:text-blue-600"
      >
        <Plus size={16} />
        Add Resource Requirement
      </button>

      {/* Resource selector */}
      {showResourceSelector && (
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
          <div className="flex justify-between items-center mb-3">
            <h4 className="font-medium">Add Resource Requirement</h4>
            <button
              type="button"
              onClick={() => setShowResourceSelector(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={20} />
            </button>
          </div>

          {/* Category tabs */}
          <div className="flex gap-2 mb-3">
            {Object.keys(resourceCategories).map(cat => (
              <button
                key={cat}
                type="button"
                onClick={() => setSelectedResourceCategory(cat)}
                className={`px-3 py-1 rounded-full text-xs font-medium ${
                  selectedResourceCategory === cat
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {resourceCategories[cat].name}
              </button>
            ))}
          </div>

          {/* Resource types */}
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {resourceCategories[selectedResourceCategory].types.map(type => (
              <button
                key={type.id}
                type="button"
                onClick={() => {
                  addResourceRequirement(selectedResourceCategory, {
                    type: type.id,
                    typeName: type.name,
                    capacityUnit: type.capacityUnit,
                    minCapacity: type.minCapacity,
                    required: true
                  });
                  setShowResourceSelector(false);
                }}
                className="w-full text-left p-2 rounded-lg hover:bg-white border border-transparent hover:border-gray-200 transition-colors"
              >
                <div className="font-medium text-sm">{type.name}</div>
                <div className="text-xs text-gray-500">
                  Min capacity: {type.minCapacity} {type.capacityUnit}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic':
        return renderBasicInfo();
      case 'skills':
        return (
          <div className="space-y-4">
            {/* Existing skills selection UI */}
            {/* Required Skills */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Required Skills * (must have ALL to qualify)
              </label>
              
              {formData.requiredSkills.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {formData.requiredSkills.map(skill => (
                    <div key={skill.id} className="flex items-center gap-1">
                      <SkillBadge skill={skill} size="small" />
                      <button
                        type="button"
                        onClick={() => handleSkillRemove(skill.id, 'required')}
                        className="text-gray-400 hover:text-red-600"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              <button
                type="button"
                onClick={() => setShowRequiredSkills(!showRequiredSkills)}
                className="flex items-center gap-2 px-3 py-1.5 border border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-blue-500 hover:text-blue-600"
              >
                <Plus size={16} />
                Add Required Skill
              </button>
            </div>

            {/* Preferred Skills */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Preferred Skills (nice to have)
              </label>
              
              {formData.preferredSkills.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {formData.preferredSkills.map(skill => (
                    <div key={skill.id} className="flex items-center gap-1">
                      <SkillBadge skill={skill} size="small" />
                      <button
                        type="button"
                        onClick={() => handleSkillRemove(skill.id, 'preferred')}
                        className="text-gray-400 hover:text-red-600"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              <button
                type="button"
                onClick={() => setShowPreferredSkills(!showPreferredSkills)}
                className="flex items-center gap-2 px-3 py-1.5 border border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-blue-500 hover:text-blue-600"
              >
                <Plus size={16} />
                Add Preferred Skill
              </button>
            </div>

            {/* Skill Selection Modal */}
            {(showRequiredSkills || showPreferredSkills) && (
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-medium">
                    Select {showRequiredSkills ? 'Required' : 'Preferred'} Skill
                  </h4>
                  <button
                    type="button"
                    onClick={() => {
                      setShowRequiredSkills(false);
                      setShowPreferredSkills(false);
                      setSkillSearch('');
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X size={20} />
                  </button>
                </div>
                
                <div className="space-y-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
                    <input
                      type="text"
                      value={skillSearch}
                      onChange={(e) => setSkillSearch(e.target.value)}
                      placeholder="Search skills..."
                      className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  
                  <div className="flex gap-2 flex-wrap">
                    {categories.map(cat => (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => setSelectedCategory(cat)}
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          selectedCategory === cat
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {cat === 'all' ? 'All Categories' : cat}
                      </button>
                    ))}
                  </div>
                  
                  <div className="max-h-40 overflow-y-auto space-y-2">
                    {getAvailableSkills().map(skill => (
                      <button
                        key={skill.id}
                        type="button"
                        onClick={() => handleSkillSelect(skill, showRequiredSkills ? 'required' : 'preferred')}
                        className="w-full text-left p-2 rounded-lg hover:bg-white border border-transparent hover:border-gray-200 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium text-sm">{skill.name}</div>
                            <div className="text-xs text-gray-500">{skill.category} • Tier {skill.tier}</div>
                          </div>
                          <div className="text-xs text-gray-600">
                            +{skill.tokenReward} tokens
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      case 'resources':
        return renderResources();
      case 'schedule':
        return (
          <div className="space-y-4">
            {/* Schedule */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Calendar size={14} className="inline mr-1" />
                  Date *
                </label>
                <input
                  type="date"
                  value={formData.schedule.date}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    schedule: { ...prev.schedule, date: e.target.value }
                  }))}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Clock size={14} className="inline mr-1" />
                  Estimated Hours *
                </label>
                <input
                  type="number"
                  value={formData.estimatedHours}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    estimatedHours: Math.max(0.5, Math.min(12, parseFloat(e.target.value) || 1))
                  }))}
                  min="0.5"
                  max="12"
                  step="0.5"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Time
                </label>
                <input
                  type="time"
                  value={formData.schedule.startTime}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    schedule: { ...prev.schedule, startTime: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Time
                </label>
                <input
                  type="time"
                  value={formData.schedule.endTime}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    schedule: { ...prev.schedule, endTime: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.schedule.flexible}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    schedule: { ...prev.schedule, flexible: e.target.checked }
                  }))}
                  className="rounded"
                />
                <span className="text-sm text-gray-700">Flexible timing (worker can negotiate)</span>
              </label>
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <MapPin size={14} className="inline mr-1" />
                Location *
              </label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., Sacramento Distribution Center"
              />
            </div>

            {/* Compensation */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Token Reward *
              </label>
              <div className="space-y-2">
                <input
                  type="range"
                  value={formData.tokenReward}
                  onChange={(e) => setFormData(prev => ({ ...prev, tokenReward: parseInt(e.target.value) }))}
                  min="10"
                  max={Math.min(maxAllowedTokens, tokenBudget.remaining)}
                  step="1"
                  className="w-full"
                />
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-4">
                    <div className="text-2xl font-bold">{formData.tokenReward}</div>
                    <div className="text-sm text-gray-600">tokens</div>
                  </div>
                  <div className="text-sm text-gray-600">
                    Platform suggests: {suggestedTokens} tokens
                  </div>
                </div>
                
                {formData.tokenReward > suggestedTokens * 1.5 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded p-2 text-xs text-yellow-800">
                    <AlertCircle size={12} className="inline mr-1" />
                    Token reward is significantly higher than platform recommendation
                  </div>
                )}
              </div>
            </div>

            {/* Trust Requirement */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Minimum Trust Score Required
              </label>
              <div className="space-y-2">
                <input
                  type="range"
                  value={formData.minTrustScore}
                  onChange={(e) => setFormData(prev => ({ ...prev, minTrustScore: parseFloat(e.target.value) }))}
                  min="3.0"
                  max="4.5"
                  step="0.1"
                  className="w-full"
                />
                <div className="flex justify-between text-sm text-gray-600">
                  <span>3.0 (Building)</span>
                  <span className="font-medium text-lg">{formData.minTrustScore}</span>
                  <span>4.5 (Expert)</span>
                </div>
              </div>
            </div>
          </div>
        );
      case 'review':
        return (
          <div className="space-y-4">
            {/* Preview */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium mb-3">Job Summary</h4>
              
              <div className="space-y-3 text-sm">
                <div>
                  <span className="font-medium text-gray-700">Title:</span>
                  <span className="ml-2">{formData.title || 'Not specified'}</span>
                </div>
                
                <div>
                  <span className="font-medium text-gray-700">Description:</span>
                  <p className="mt-1 text-gray-600">{formData.description || 'Not specified'}</p>
                </div>
                
                <div>
                  <span className="font-medium text-gray-700">Required Skills:</span>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {formData.requiredSkills.map(skill => (
                      <SkillBadge key={skill.id} skill={skill} size="small" />
                    ))}
                  </div>
                </div>
                
                {formData.preferredSkills.length > 0 && (
                  <div>
                    <span className="font-medium text-gray-700">Preferred Skills:</span>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {formData.preferredSkills.map(skill => (
                        <SkillBadge key={skill.id} skill={skill} size="small" />
                      ))}
                    </div>
                  </div>
                )}
                
                {Object.values(formData.resourceRequirements).some(reqs => reqs.length > 0) && (
                  <div>
                    <span className="font-medium text-gray-700">Resource Requirements:</span>
                    <div className="mt-1 space-y-1">
                      {Object.entries(formData.resourceRequirements).map(([category, reqs]) => 
                        reqs.map((req, index) => (
                          <div key={`${category}-${index}`} className="flex items-center gap-2 text-xs">
                            {category === 'vehicles' && <Truck size={12} />}
                            {category === 'equipment' && <Wrench size={12} />}
                            {category === 'spaces' && <Building size={12} />}
                            <span>{req.typeName}</span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="font-medium text-gray-700">Schedule:</span>
                    <p className="text-gray-600">
                      {formData.schedule.date ? new Date(formData.schedule.date).toLocaleDateString() : 'Not set'}
                      {formData.schedule.flexible && ' (Flexible)'}
                    </p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Hours:</span>
                    <span className="ml-2">{formData.estimatedHours}</span>
                  </div>
                </div>
                
                <div>
                  <span className="font-medium text-gray-700">Location:</span>
                  <span className="ml-2">{formData.location || 'Not specified'}</span>
                </div>
                
                <div className="pt-2 border-t">
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="font-medium text-gray-700">Token Reward:</span>
                      <span className="ml-2 text-lg font-bold">{formData.tokenReward} tokens</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Min Trust:</span>
                      <span className="ml-2">{formData.minTrustScore}</span>
                    </div>
                  </div>
                </div>
                
                <div className="pt-2 border-t">
                  <p><strong>Estimated qualified members:</strong> {getQualifiedUsersEstimate()}</p>
                  <p><strong>Token cost:</strong> {formData.tokenReward} tokens</p>
                  <p><strong>Hourly rate:</strong> {Math.round(formData.tokenReward / formData.estimatedHours)} tokens/hour</p>
                  <p><strong>Budget remaining after posting:</strong> {tokenBudget.remaining - formData.tokenReward} tokens</p>
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="max-h-[80vh] overflow-y-auto">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Token Budget Display */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex justify-between items-center mb-2">
            <h4 className="font-medium text-blue-900">Your Token Budget</h4>
            <HelpCircle 
              size={16} 
              className="text-blue-600 cursor-help" 
              title="Tokens are allocated monthly based on your trust score and group activity"
            />
          </div>
          <div className="relative bg-blue-100 rounded-full h-4 mb-2">
            <div 
              className="absolute top-0 left-0 h-full bg-blue-500 rounded-full transition-all"
              style={{ width: `${((tokenBudget.monthly - tokenBudget.remaining) / tokenBudget.monthly) * 100}%` }}
            />
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-blue-700">Used: {formatTokenAmount(tokenBudget.monthly - tokenBudget.remaining)}</span>
            <span className="text-blue-900 font-medium">
              Remaining: {formatTokenAmount(tokenBudget.remaining)} / {formatTokenAmount(tokenBudget.monthly)}
            </span>
          </div>
          <p className="text-xs text-blue-600 mt-2">
            Budget resets monthly. Higher trust scores unlock larger budgets.
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
            <AlertCircle className="text-red-600 mt-0.5" size={16} />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-4 overflow-x-auto">
          {tabs.map((tab, index) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {renderTabContent()}

        {/* Actions */}
        <div className="flex gap-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            disabled={loading}
          >
            Cancel
          </button>
          {activeTab !== 'review' ? (
            <button
              type="button"
              onClick={() => {
                const currentIndex = tabs.findIndex(t => t.id === activeTab);
                if (currentIndex < tabs.length - 1) {
                  setActiveTab(tabs[currentIndex + 1].id);
                }
              }}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
            >
              Next
            </button>
          ) : (
            <button
              type="submit"
              disabled={loading || formData.tokenReward > tokenBudget.remaining}
              className={`flex-1 px-4 py-2 rounded-lg font-medium ${
                loading || formData.tokenReward > tokenBudget.remaining
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <Loader className="animate-spin mr-2" size={16} />
                  Posting...
                </span>
              ) : (
                'Post Job'
              )}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default JobPostingForm;